import TIME_RANGE from '../constants/constants';
import moment from 'moment';
import posthog from 'posthog-js';

export const isAbsoluteURL = (link: string) => {
  const absoluteURLRegex = /^(http:\/\/|https:\/\/|ftp:\/\/|www\.[a-z]+)/i;
  return absoluteURLRegex.test(link);
};

export const isRelativeURLWithoutSlash = (link: string) => {
  const relativeURLRegex = /^[^/].*/;
  return relativeURLRegex.test(link);
};

export const formatDate = (date: any) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  const day = String(date.getDate()).padStart(2, '0');

  // Define an array of month names
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const monthIndex = date.getMonth(); // Months are zero-based
  const month = monthNames[monthIndex];

  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const formatTime = (date: any) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

export const handleDownloadCSV = (data: any) => {
  // Create an array to hold the CSV data
  const csvData = [];

  // Extract and store labels
  const labels = data.datasets.map((obj: any) => obj.label);

  // Insert Date-Time as First column header
  labels.splice(0, 0, 'Date-Time');

  // Add labels as the first row
  csvData.push(labels);

  // Calculate the maximum number of data points among all objects
  const maxDataPoints = Math.max(
    ...data.datasets.map((obj: any) => (Array.isArray(obj.data) ? obj.data.length : 1))
  );

  // Iterate through the data and transform it into the desired CSV format
  for (let i = 0; i < maxDataPoints; i++) {
    const row = data.datasets.map((obj: any) => (Array.isArray(obj.data) ? obj.data[i] : obj.data));
    // Push Date-Time value in first column
    row.splice(0, 0, data.labelsWithYear[i]);
    csvData.push(row);
  }

  // Create a CSV string by joining the rows with line breaks
  const csvString = csvData.map(row => row.join(',')).join('\n');

  // Create a Blob containing the CSV data
  const blob = new Blob([csvString], { type: 'text/csv' });

  // Create a temporary URL to the Blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary anchor element to trigger the download
  const a = document.createElement('a');
  a.href = url;
  a.download = 'widgetData.csv'; // Set the desired filename

  // Trigger a click event on the anchor element to initiate the download
  a.click();

  // Clean up resources
  window.URL.revokeObjectURL(url);
};

export const startDateTimeRangeNumberWidget = (customTimeRangeSettings: any, timeFrame: any) => {
  if (customTimeRangeSettings?.startTime && customTimeRangeSettings?.endTime) {
    const startTime = new Date(customTimeRangeSettings.startTime);
    return `${formatDateTime(startTime)}`;
  }
  return moment(timeFrame?.startDate?.toString()).format('MMM DD YYYY');
};

export const endDateTimeRangeNumberWidget = (customTimeRangeSettings: any, timeFrame: any) => {
  if (customTimeRangeSettings?.startTime && customTimeRangeSettings?.endTime) {
    const endTime = new Date(customTimeRangeSettings.endTime);
    return `${formatDateTime(endTime)}`;
  }
  return moment(timeFrame?.endDate?.toString()).format('MMM DD YYYY');
};

const formatDateTime = (date: Date) => {
  const dateString = date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
  });
  const timeString = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateString} ${timeString}`;
};

export const handleDownloadCSVForRangeWidget = (
  data: any,
  customTimeRangeSettings: any,
  timeFrame: any
) => {
  // Create an array to hold the CSV data
  const csvData = [];

  // Extract and store labels
  const labels = data.datasets.map((obj: any) => obj.label);

  // Insert Date-Time as First column header
  labels.splice(0, 0, 'Date-Time', 'Percentage Change');

  // Add labels as the first row
  csvData.push(labels);

  // Calculate the maximum number of data points among all objects

  // Iterate through the data and transform it into the desired CSV format
  const row = [data.datasets[0]?.data?.[0]];
  // Push Date-Time value in first column
  row.splice(
    0,
    0,
    `${startDateTimeRangeNumberWidget(customTimeRangeSettings, timeFrame)}-${endDateTimeRangeNumberWidget(customTimeRangeSettings, timeFrame)}`,
    data.datasets[0]?.percentageChange[0]
  );
  csvData.push(row);

  // Create a CSV string by joining the rows with line breaks
  const csvString = csvData?.map(row => row.join(',')).join('\n');

  // Create a Blob containing the CSV data
  const blob = new Blob([csvString], { type: 'text/csv' });

  // Create a temporary URL to the Blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary anchor element to trigger the download
  const a = document.createElement('a');
  a.href = url;
  a.download = 'widgetData.csv'; // Set the desired filename

  // Trigger a click event on the anchor element to initiate the download
  a.click();

  // Clean up resources
  window.URL.revokeObjectURL(url);
};

export const handleDownloadCSVForManualWidget = (data: any) => {
  // Create an array to hold the CSV data
  const csvData = [];

  // Define the column headers
  const headers = ['Details', 'Plant Name', 'Date Created', 'Type', 'SubType', 'Headers', 'Description', 'Pinned Comment'];
  csvData.push(headers); // Add headers as the first row

  // Iterate over the alert data to extract relevant fields
  data?.forEach((alert: any) => {
    const details = alert?.heading?.eng; // Extract alert heading
    const plantName = alert?.plantName || 'Unknown Plant'; // Extract plant name
    const dateCreated = alert.lastTicket?.createdAt || 'No Date'; // Extract date created
    const type = alert?.issueType?.join(', '); // Extract issue types
    const subType = alert?.subIssueType || 'N/A'; // Extract sub issue type
    const headers =
      alert?.tags?.length > 0 ? alert.tags.map((tag: any) => tag?.name).join(', ') : 'No Tags'; // Extract alert tags
    const desc = alert?.description?.eng || 'N/A';
    const pinnedComment = alert?.tickets[0]?.pinnedComment || 'N/A';
    // Combine all extracted fields into a CSV row
    const row = [details, plantName, dateCreated, type, subType, headers, desc, pinnedComment];
    csvData.push(row); // Add the row to CSV data
  });

  // Create a CSV string by joining the rows with line breaks
  const csvString = csvData.map(row => row.join(',')).join('\n');

  // Create a Blob containing the CSV data
  const blob = new Blob([csvString], { type: 'text/csv' });

  // Create a temporary URL to the Blob
  const url = window.URL.createObjectURL(blob);

  // Create a temporary anchor element to trigger the download
  const a = document.createElement('a');
  a.href = url;
  a.download = 'alertData.csv'; // Set the desired filename

  // Trigger a click event on the anchor element to initiate the download
  a.click();

  // Clean up resources
  window.URL.revokeObjectURL(url);
};

export const calculateTimeFrame = (defaultTimeRange: any) => {
  let {
    unit,
    unitMultiplier,
    timeRangeType,
    // timeRangeInMins,
    startTime,
    endTime,
  } = defaultTimeRange;

  if (!(startTime instanceof Date)) {
    startTime = new Date(startTime);
  }

  if (!(endTime instanceof Date)) {
    endTime = new Date(endTime);
  }

  if (timeRangeType === 'absolute') {
    return { startDate: startTime, endDate: endTime };
  } else if (timeRangeType === 'relative') {
    let timeRange: any, dateRange: any;
    if (['hours', 'minutes'].includes(unit)) {
      timeRange = TIME_RANGE.filter((time: any) => {
        if (time.unit == unit) {
          return time;
        }
      })[0];
      dateRange = timeRange.getDateRange(unitMultiplier);
    } else {
      timeRange = TIME_RANGE.filter((time: any) => {
        if (time.unit == unit && time.value == unitMultiplier) {
          return time;
        }
      })[0];
      dateRange = timeRange.getDateRange();
    }

    const startDate = dateRange[0].$d;
    const endDate = dateRange[1].$d;

    return { startDate, endDate };
  }

  return null;
};

type EventProperties = Record<string, any>;
type UserProperties = Record<string, any>;

interface IdentifyUserOptions {
  distinctId?: string;
  userProperties: UserProperties;
  eventProperties?: EventProperties;
}

export const posthogIdentify = async ({
  distinctId,
  userProperties = {},
  eventProperties = {},
}: IdentifyUserOptions): Promise<void> => {
  try {
    const exitingDistinctId = posthog.get_distinct_id();
    if(exitingDistinctId?.length === 24 || !exitingDistinctId?.includes('-')) {
      return;
    };

    if (typeof posthog !== 'undefined') {
      if (distinctId) {
        posthog.identify(distinctId, userProperties, eventProperties);
      } else {
        posthog.people.set(userProperties);
      }
    }
  } catch (error) {
    throw error;
  }
};

export const posthogReset = () => {
  if (typeof posthog !== 'undefined') {
    posthog.reset();
  }
}

export const handleScadaQuestionValidation = (name: string, value: any, index: number, formState: any[] | null) => {
  const newFormState: any = formState ? [...formState] : [];
  let isValidData = true;
  if (newFormState[index]?.validation?.length) {
    let rangeValue: any = [];
    if(newFormState[index]?.validation?.includes('range')) {
      rangeValue = value.split('-');
    }
    newFormState[index].validation?.forEach((rule: string) => {
      if (value !== '' && rule === 'number' && !/^\d+$/.test(value)) {
        isValidData = false;
      }
      if(value !== '' && rule === 'email' && !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value)) {
        isValidData = false;
      }
      if(value !== '' && rule === 'phone' && !/^\d{10}$/.test(value)) {
        isValidData = false;
      }
      if(value !== '' && rule === 'string' && !/^[a-zA-Z\s]+$/.test(value)) {
        isValidData = false;
      }
      if(value !== '' && rule === 'range' && !Number.isNaN(value)) {
        let rangeValues = newFormState[index]?.rangeValues || [];
        let [start, end] = rangeValues;
        if((!start) || (!end) || (start > end) || (start < 0) || (end < 0)) {
          isValidData = false;
        }
      }
    });
  }
  if (!isValidData) return;
  newFormState[index][name] = value;
  newFormState[index]['error'] = '';
  return newFormState;
};



export const handleCopyToClipboard = async (
  textToCopy: string,
  setSnackbarState: (state: { open: boolean; message: string }) => void
) => {
  if (!textToCopy || typeof textToCopy !== 'string') {
    setSnackbarState({
      open: true,
      message: 'Nothing to copy',
    });
    return;
  }

  if (!navigator?.clipboard?.writeText) {
    setSnackbarState({
      open: true,
      message: 'Clipboard API not supported in this browser',
    });
    return;
  }

  try {
    await navigator.clipboard.writeText(textToCopy);
    setSnackbarState({
      open: true,
      message: 'Copied to clipboard',
    });
  } catch (error) {
    setSnackbarState({
      open: true,
      message: 'Failed to copy to clipboard',
    });
  }
};


export const downloadFileFromUrl = async(fileUrl: string, fileName?: string) => {
  try {
    const response = await fetch(fileUrl, { mode: 'cors' });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    throw e;
  }
};

export const getFileTypeFromMime = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType.startsWith('video/')) return 'Video';
  if (mimeType.startsWith('audio/')) return 'Audio';

  switch (mimeType) {
    case 'application/pdf':
      return 'PDF';
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'Word';
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'text/csv':
      return 'Excel';
    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'PPT';
    case 'text/plain':
      return 'Text';
    default:
      return '';
  }
};

export const getIconTypeFromMime = (mime: string): string | null => {
  if (mime.startsWith('image/')) return 'image_icon';
  if (mime.startsWith('video/')) return 'video_icon';
  if (mime.startsWith('audio/')) return 'audio_icon';

  switch (mime) {
    case 'application/pdf':
      return 'pdf_icon';
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'doc_icon';
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'text/csv':
      return 'excel_icon';
    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'ppt_icon';
    case 'text/plain':
      return 'text_icon';
    // case 'application/zip':
    // case 'application/x-7z-compressed':
    // case 'application/x-rar-compressed':
    //   return 'zip_icon';
    default:
      return null;
  }
};

export const getZoomToFitValue = () => ({useModelGeometry: true, padding: 20});
export const EquipmentLabelProps = {
  style: {
    fontSize: '24px',
    letterSpacing: '1px',
    fontWeight: 'bolder',
    textWrap: {
      ellipsis: true,
      lineClamp: 3,
      whiteSpace: 'nowrap',
      width: '80%',
    }
  },
}
export const EquipmentLabelPropsWithoutWrap = {
  style: {
    fontSize: '24px',
    letterSpacing: '1px',
    fontWeight: 'bolder',
  },
}

export const equipmentLabelPropsWithCustomWidth = (width: string) => {
  return {
    style: {
      fontSize: '24px',
      letterSpacing: '1px',
      fontWeight: 'bolder',
      textWrap: {
        ellipsis: true,
        lineClamp: 3,
        whiteSpace: 'nowrap',
        width: width,
      }
    },
  }
}